import React, { useCallback, useState } from "react";
import { Alert, DeviceEventEmitter, Platform } from "react-native";
import Toast from "react-native-toast-message";

import { useEventAuth } from "@/components/teams/useWorkspaceAuth";
import { useTeamAccess } from "@/components/teams/useWorkspace";
import { hasOwnTeam } from "@/components/teams/flag";
import { DONATIONS_ENABLED } from "@/components/donations/config";
import { useActionSheet } from "@/components/hooks/useActionSheet";
import useNow from "@/components/hooks/useNow";
import { deleteEvent } from "@/components/http/events";
import useAddRoom from "@/components/rooms/useAddRoom";
import useRoomGroup, { ROOMS_CHANGED } from "@/components/rooms/useRoomGroup";
import {
  canAddRoom,
  isGrouped,
  isRoom,
  roomLabelOf,
  roomNounOf,
} from "@/components/rooms/rooms";
import { addsCameras, isExtraCamera } from "@/components/rooms/multicam";
import { isEventArchived } from "@/components/shared/eventStorageUtils";
import { t, tn } from "@/localization";

// "Are you sure you want to delete Gala and its 2 stages?": a whole sentence
// for each room noun (rooms.js ROOM_NOUNS, lowercased), so each language can
// agree its words with the noun and the count. A new noun needs its own case.
const deleteGroupQuestion = (noun, count, title) => {
  const vars = { title };
  switch (noun) {
    case "stage":
      return tn(
        "Are you sure you want to delete {title} and its {count} stage?",
        "Are you sure you want to delete {title} and its {count} stages?",
        count,
        vars
      );
    case "track":
      return tn(
        "Are you sure you want to delete {title} and its {count} track?",
        "Are you sure you want to delete {title} and its {count} tracks?",
        count,
        vars
      );
    case "court":
      return tn(
        "Are you sure you want to delete {title} and its {count} court?",
        "Are you sure you want to delete {title} and its {count} courts?",
        count,
        vars
      );
    case "field":
      return tn(
        "Are you sure you want to delete {title} and its {count} field?",
        "Are you sure you want to delete {title} and its {count} fields?",
        count,
        vars
      );
    case "hall":
      return tn(
        "Are you sure you want to delete {title} and its {count} hall?",
        "Are you sure you want to delete {title} and its {count} halls?",
        count,
        vars
      );
    case "camera":
      return tn(
        "Are you sure you want to delete {title} and its {count} camera?",
        "Are you sure you want to delete {title} and its {count} cameras?",
        count,
        vars
      );
    default:
      return tn(
        "Are you sure you want to delete {title} and its {count} room?",
        "Are you sure you want to delete {title} and its {count} rooms?",
        count,
        vars
      );
  }
};

// The event settings menu: the list under the event on phones (EventEdit) and
// the sidebar on wide layouts (components/layout/EventSidebar.js). Items with
// `screen` open that screen; items with `onPress` act in place (delete).
// Rooms are renamed on their chip (RoomSwitcher). Render `modals` once next to
// the menu. `switchRoom` opens another
// room of the event in place (a room just added, for one); `openScreen(name)`
// opens one of the event's screens the way its menu does (default: navigate).
//
// Teams: in someone else's account each item needs the `permission` it names
// (components/teams/roles.js); a moderator sees only the guestbook. In your own
// account everything shows, plus People when the Teams UI is on.
const useEventMenu = ({ event, slug, navigation, switchRoom, openScreen }) => {
  const { refetchProfile, account_type } = useEventAuth(event);
  const team = useTeamAccess();
  const allowed = (permission) => !permission || team.can(permission, event);
  const { showActionSheetWithOptions } = useActionSheet();
  const [deletingEvent, setDeletingEvent] = useState(false);

  // Rooms: this event's group (the primary and its rooms), if any.
  const { primary: groupPrimary, rooms: groupRooms } = useRoomGroup(event);
  const { openAddRoom } = useAddRoom({
    navigation,
    showIntro: () =>
      openScreen
        ? openScreen("AddRoom")
        : navigation.navigate("AddRoom", { event, slug }),
  });
  const roomPrimary = isRoom(event) ? groupPrimary : event;
  const roomsAddable =
    allowed("addRoom") &&
    !!roomPrimary?.slug &&
    canAddRoom(roomPrimary, { account_type }, groupRooms.length);
  // Teams: rooms are renamed on their chip; it takes the role that edits the event.
  const roomsRenamable = allowed("editEvent");
  const roomNoun = roomNounOf(groupPrimary || event).toLowerCase();
  const onAddRoomPress = () =>
    roomPrimary &&
    openAddRoom(roomPrimary, groupRooms.length, { openRoom: true, switchRoom });

  const now = useNow();
  const isAfter = now.isAfter(event.event_window_end_datetime_utc);
  const archived = isEventArchived(event, account_type, now);
  const { title } = event;

  const confirmDeleteEvent = useCallback(async () => {
    if (deletingEvent) return;

    setDeletingEvent(true);
    try {
      // An event's rooms go first: the server keeps an event with rooms.
      const roomsFirst = isRoom(event) ? [] : groupRooms;
      for (const room of roomsFirst) {
        await deleteEvent(room.slug);
        DeviceEventEmitter.emit("event_delete", room.slug);
      }
      await deleteEvent(slug);
      DeviceEventEmitter.emit("event_delete", slug);
      if (isRoom(event)) {
        DeviceEventEmitter.emit(ROOMS_CHANGED);
      }
      await refetchProfile();
      navigation.navigate("EventList");
    } catch (err) {
      Toast.show({
        text1: t("Could not delete event"),
        text2: err?.errors?.[0]?.message || err?.message,
        type: "error",
      });
    } finally {
      setDeletingEvent(false);
    }
  }, [deletingEvent, navigation, refetchProfile, slug, event, groupRooms]);

  const deleteQuestion =
    !isRoom(event) && groupRooms.length
      ? deleteGroupQuestion(roomNoun, groupRooms.length, title)
      : t("Are you sure you want to delete {title}?", {
          title: isRoom(event) ? roomLabelOf(event) : title,
        });

  const onDeleteEventPress = useCallback(() => {
    if (deletingEvent) return;

    if (Platform.OS === "ios" || Platform.OS === "android") {
      Alert.alert(
        deleteQuestion,
        t("This action cannot be undone."),
        [
          { text: t("Cancel"), style: "cancel" },
          {
            text: t("Delete Event"),
            style: "destructive",
            onPress: () => {
              void confirmDeleteEvent();
            },
          },
        ],
        { cancelable: true }
      );
      return;
    }

    showActionSheetWithOptions(
      {
        options: [t("Delete Event"), t("Cancel")],
        cancelButtonIndex: 1,
        destructiveButtonIndex: 0,
        title: deleteQuestion,
        message: t("This action cannot be undone."),
      },
      (buttonIndex) => {
        if (buttonIndex !== 0) return;
        void confirmDeleteEvent();
      }
    );
  }, [
    confirmDeleteEvent,
    deletingEvent,
    showActionSheetWithOptions,
    deleteQuestion,
  ]);

  // A camera other than the main one: its guests, tickets, privacy, guestbook
  // and fundraiser are the main camera's (multicam.js).
  const extraCamera = isExtraCamera(event);

  const deleteMenuItem = {
    title: deletingEvent
      ? t("Deleting Event...")
      : extraCamera
      ? t("Delete {camera}", { camera: roomLabelOf(event) })
      : t("Delete Event"),
    icon: "trash-outline",
    destructive: true,
    hideChevron: true,
    onPress: onDeleteEventPress,
    disabled: deletingEvent,
    permission: "deleteEvent",
  };

  // Fundraising (Pledge) is hidden until the evt.live API ships for this build.
  // It sits right below Sell Tickets.
  const fundraisingMenuItem = DONATIONS_ENABLED
    ? {
        title: t("Fundraising"),
        icon: "heart-outline",
        screen: "EventDonations",
        permission: "fundraising",
      }
    : null;

  // Teams: who else can work on this event (not on tests, which are yours).
  // Business accounts' events; in someone else's account `account_type` is the
  // owner's, and the role decides (manageTeam).
  const peopleMenuItem =
    team.enabled && !event.is_test && hasOwnTeam(account_type)
      ? {
          title: t("People"),
          icon: "person-add-outline",
          screen: "EventPeople",
          permission: "manageTeam",
        }
      : null;

  const items = archived
    ? [deleteMenuItem]
    : extraCamera
    ? [
        ...(!isAfter
          ? [
              {
                title: t("How to Go Live"),
                icon: "videocam",
                screen: "HowToStream",
                permission: "goLive",
              },
            ]
          : []),
        {
          title: t("Manage Video"),
          icon: "cloud-upload",
          screen: "ReuploadVideo",
          permission: "video",
        },
        deleteMenuItem,
      ]
    : [
        ...(!isAfter
          ? [
              {
                title: t("How to Go Live"),
                icon: "videocam",
                screen: "HowToStream",
                permission: "goLive",
              },
            ]
          : []),
        {
          title: t("Guest List & Invite"),
          icon: "people",
          screen: "GuestList",
          permission: "guests",
        },
        {
          title: t("Title, Date, Time, Event Link, Uploads"),
          icon: "information-circle",
          screen: "EventDetails",
          permission: "editEvent",
        },
        ...(roomsAddable && !isGrouped(event)
          ? [
              addsCameras({ account_type })
                ? {
                    title: t("Add Another Camera Angle"),
                    icon: "videocam-outline",
                    screen: "AddRoom",
                    permission: "addRoom",
                  }
                : {
                    title: t("Add a Room, Stage or Track"),
                    icon: "albums-outline",
                    screen: "AddRoom",
                    permission: "addRoom",
                  },
            ]
          : []),
        {
          title: t("Manage Video"),
          icon: "cloud-upload",
          screen: "ReuploadVideo",
          permission: "video",
        },
        {
          title: event.prefer_chat ? t("Chat") : t("Guestbook"),
          icon: event.prefer_chat ? "chatbubbles" : "book",
          screen: "EventGuestbook",
          permission: "moderate",
        },
        {
          title: t("Privacy Settings"),
          icon: "shield",
          screen: "EventPrivacy",
          permission: "editEvent",
        },
        ...(account_type === "business"
          ? [
              {
                title: t("Event Branding"),
                icon: "brush",
                screen: "EventBranding",
                permission: "editEvent",
              },
              {
                title: t("Event Analytics"),
                icon: "analytics",
                screen: "EventAnalytics",
                permission: "analytics",
              },
            ]
          : []),

        {
          title: t("Sell Tickets"),
          icon: "card",
          screen: "SellTickets",
          permission: "sellTickets",
        },
        ...(fundraisingMenuItem ? [fundraisingMenuItem] : []),
        {
          title: t("Embed"),
          icon: "code",
          screen: "Embed",
          permission: "editEvent",
        },
        ...(peopleMenuItem ? [peopleMenuItem] : []),
        deleteMenuItem,
      ];
  const menu = items.filter((item) => allowed(item.permission));

  // Nothing at the moment: kept for the menu's dialogs.
  const modals = null;

  return {
    menu,
    modals,
    now,
    isAfter,
    archived,
    groupPrimary,
    groupRooms,
    roomsAddable,
    roomsRenamable,
    roomNoun,
    onAddRoomPress,
  };
};

export default useEventMenu;
